/**
 * 用户编辑器悬浮解组按钮，依赖当前图层数据和 VIP 水印服务同步操作权限
 */
import { useCallback, useSyncExternalStore } from 'react';
import { getLocaleStore } from '@/i18n';
import type { IShortcutMenuItemRenderArgs } from '@/plugins/shortcut-menu/type';
import { ShortcutToolbarIconItem } from '@/plugins/shortcut-menu/views/shortcut-toolbar-widgets';
import { LayerType, type IGroupLayerProps } from '@/version-types';
import { GroupLayerHotkeyType } from '../../../../type';

/**
 * 在悬浮工具栏挂载时按图层 ID 订阅解组权限，VIP 判定变化后更新提示和点击行为
 *
 * @param props - 当前工具栏的框架实例和主图层 ID
 * @returns 解组按钮，不支持展示的图层返回 null
 */
export const GroupUnpackShortcutItem = (props: Pick<IShortcutMenuItemRenderArgs, 'framework' | 'id'>) => {
    const { framework, id } = props;
    const { $t } = getLocaleStore();
    const subscribe = useCallback(
        (onChange: () => void) => {
            const { events, flatLayer, svipWatermark } = framework.service;
            const watermarkHandler = svipWatermark.onWatermark(onChange);
            // VIP 标记可能晚于水印事件写回，按当前 ID 监听图层以覆盖两种时序。
            const layerHandler = flatLayer.onChange(onChange, id);
            return () => {
                events.off(watermarkHandler);
                events.off(layerHandler);
            };
        },
        [framework, id],
    );
    const getSnapshot = useCallback(() => {
        const { flatLayer } = framework.service;
        const layer = flatLayer.getLayer(id);
        if (!layer || layer.layerType !== LayerType.GROUP_LAYER || (layer.data as IGroupLayerProps)?.isExtraGroup) {
            return null;
        }
        if (flatLayer.checkBrokenGroup(layer)) return 'enabled';
        return flatLayer.checkIsGroupExtension(layer) ? null : 'disabled';
    }, [framework, id]);
    const action = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

    if (!action) return null;

    if (action === 'enabled') {
        return (
            <ShortcutToolbarIconItem
                name="DelinkGroupBold"
                title={$t((v) => v.baseLayer.shortcutOperate.unGroup)}
                onClick={(e) => {
                    e.stopPropagation();
                    framework.service.hotkey.__emit(GroupLayerHotkeyType.UNPACK, { from: 'shortcut-menu' });
                }}
            />
        );
    }
    return (
        <ShortcutToolbarIconItem
            name="DelinkGroupBold"
            title={$t((v) => v.baseLayer.shortcutOperate.unSupportGroup)}
            onClick={() => {}}
        />
    );
};
